import React, { createContext, useState, useEffect, useContext } from 'react';
import { getCsrfToken, fetchCsrfToken } from '../utils/csrf';


const AuthContext = createContext(null);
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch CSRF token on app initialization
    fetchCsrfToken().catch(() => {
      // Silently fail if CSRF endpoint is unavailable
    });
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setOrganization(data.organization);
      } else {
        setUser(null);
        setOrganization(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setOrganization(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      // Fetch CSRF token before login
      await fetchCsrfToken();
      
      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }
      const response = await fetch(`${API_BASE_URL}/auth/login/`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        // Fetch organization info
        await checkAuth();
        // Refresh CSRF token after successful login
        await fetchCsrfToken();
        return { success: true, data };
      } else {
        const error = await response.json();
        return { success: false, error: error.error || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      // Fetch CSRF token before logout
      await fetchCsrfToken();
      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }
      const response = await fetch(`${API_BASE_URL}/auth/logout/`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });
      if (response.ok) {
        setUser(null);
        setOrganization(null);
        // Fetch new CSRF token after logout (new session)
        await fetchCsrfToken();
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Don't clear state on error - session might still be valid
    }
  };

  const value = {
    user,
    organization,
    loading,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

